import { logger } from '@lessonup/logging';
import { ButtonAnchor, IconHelp, toast } from '@lessonup/ui-components';
import { TFunction } from 'i18next';
import { debounce } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useEditorTranslation } from './useEditorTranslation';

interface ClipboardErrorHandlerHookResult {
  handleClipboardError: (error: Error) => void;
}

export function useClipboardErrorHandler(): ClipboardErrorHandlerHookResult {
  const { t } = useEditorTranslation();

  // Prevent logging multiple errors in a short time, for example when user holds down the cut/copy/paste keys
  const debouncedLogger = useMemo(
    () =>
      debounce((error: Error) => {
        logger.warn(error);
      }, 500),
    []
  );

  const handleClipboardError = useCallback(
    (error: Error) => {
      toast({
        title: t('clipboardErrorTitle'),
        message: t('clipboardErrorMessage'),
        button: <HelpLinkButton t={t} />,
        type: 'neutral',
        options: {
          position: 'top-right',
          autoClose: false,
          toastId: 'clipboardError',
        },
      });

      debouncedLogger(error);
    },
    [debouncedLogger, t]
  );

  return { handleClipboardError };
}

const HelpLinkButton: React.FC<{ t: TFunction }> = ({ t }) => (
  <ButtonAnchor buttonType="secondary" iconStart={<IconHelp />} target="_blank" href={t('clipboardErrorHelpLink')}>
    {t('clipboardErrorHelpButton')}
  </ButtonAnchor>
);
